import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import { MapContainer, TileLayer, Polygon, Tooltip} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {currencyFormat, getMaxMin} from '../../../utils/helpsers';
import {nullText} from '../../atoms/NullDisplay/NullDisplay';
import Box from "@mui/material/Box";
// Styles
import {MapViewStyles as styles} from './MapView.styles';

/**
 * Component to render a US Ohio states map using leaflet library.
 * @param {array} data:
 *  - data: Array of values to build the polygon.
 *  - color: The polygon color.
 *  - name: District name.
 * @param {number} districtIrn - Number ID of the reference district
 * @param {number} zoom - The amount of zoom applied on map load.
 * @param {string} height - Define the map height by pixels (example: "400px").
 * @param {string} choroplethVar - The variable machine name to build the choropleth feature.
 * @param {string} type - The type of visualization of the map.
 * @param {object} year - It is required to display the variable year if MCD.
 * @returns {JSX.Element}
 */
const MapView = ({ data, districtIrn, zoom = window.innerWidth <= 1023 ? 6.4 : 7.5, height, choroplethVar, type = 'insight', year}) => {
  const [varRange, setVarRange] = useState();
  const internationalNumberFormat = new Intl.NumberFormat('en-US');
  const ohioLocation = [40.1, -82.696216];
  const choroplethColors = {
      'null': '#cccccc4D',
      0: '#C3BAEC',
      10: '#B2A6E7',
      20: '#907EDB',
      30: '#7F6BD5',
      40: '#6E57CF',
      50: '#5D44C9',
      60: '#5239C1',
      70: '#4832A8',
      80: '#37267D',
      90: '#1F1646',
      100: '#191237',
  }

  // choroplethVar validation and data process.
    useEffect(() => {
        if(data && choroplethVar) {
            setVarRange(getMaxMin({data, key: choroplethVar}));
        }
    }, [choroplethVar, data]);

    function setChoroplethColor({item, range, key}) {
        let keyColor = item[key] === null
            ? 'null'
            :  Math.floor(((item[choroplethVar] - range?.min) / (range?.max - range?.min))*10)*10
            // Assign a default value (100) if the color calculation results in NaN
            if (keyColor !== 'null' && isNaN(keyColor)) {
                keyColor = 100;
              }
        return choroplethColors[keyColor]
    }

  return (
      <Box className="MapViewer" sx={styles}>
        <MapContainer attributionControl={false} center={ohioLocation} zoom={zoom} scrollWheelZoom={false} zoomControl={true} zoomSnap={0.25} zoomDelta={.25} style={{height: height}}>
          <TileLayer
              attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {
            data.map((location, index) =>
                location.data && (
                    <Polygon key={index} positions={location.data}
                             color={location.irn === districtIrn ? "#FFC300" : "#f7f5e54f"}
                             pathOptions={{
                                 fillColor:
                                     location.irn === districtIrn
                                         ? "#FFC300"
                                         : !choroplethVar
                                             ? location.color ?? "#4646FF"
                                             : setChoroplethColor({
                                                 item: location,
                                                 range: varRange,
                                                 key: choroplethVar,
                                                 choroplethColors: choroplethColors
                                             })
                              }}
                             fillOpacity={.9}
                             weight={1}>
                      <Tooltip>
                        <div>{location.name}</div>
                        <div>Physical Address: <strong>{location.physical_address ? location.physical_address: nullText("NR")}</strong></div>
                          {
                              type === 'insight' &&
                              <>
                                  <div>Enrolled ADM {
                                      location.total_year_end_enrollment_fy1_yr && ` (${location.total_year_end_enrollment_fy1_yr})`}
                                      : <strong>{location.total_year_end_enrollment_fy1 ? internationalNumberFormat.format(parseInt(location.total_year_end_enrollment_fy1)) : nullText("NR")}</strong></div>
                                  <div>Graduation Rate (4 Year Rate) {
                                      location.four_year_graduation_rate_yr && ` (${location.four_year_graduation_rate_yr})`}
                                      : <strong>{location.four_year_graduation_rate ? location.four_year_graduation_rate + "%" : nullText("NR")}</strong></div>
                                  <div>State Revenue As % Of Total {
                                      location.state_revenue_as_percent_of_total_fy1_yr && ` (${location.state_revenue_as_percent_of_total_fy1_yr})`}
                                      : <strong>{location.state_revenue_as_percent_of_total_fy ? location.state_revenue_as_percent_of_total_fy + "%": location.state_revenue_as_percent_of_total_fy1 ? location.state_revenue_as_percent_of_total_fy1 + "%": nullText("NR")}</strong></div>
                                  <div>Total Actual Expenses {
                                      location.total_expenditures_5_050_yr && ` (${location.total_expenditures_5_050_yr})`}
                                      : <strong>{location.total_expenditures_5_050? currencyFormat(location.total_expenditures_5_050) : nullText("NR")}</strong></div>
                                  <div>Households with Broadband Access (ACS) {
                                      location.et_broadband_of_any_type_yr && ` (${location.et_broadband_of_any_type_yr})`}
                                      : <strong>{location.et_broadband_of_any_type ? location.et_broadband_of_any_type + "%" : nullText("NR")}</strong></div>
                              </>
                          }
                          {
                              type === 'salary_ba' && (
                                  <>
                                      <div className={'choropleth-var'}>
                                          Base Salary (BA){`${year?.value === "MCD" ? `(${location.mcd_year})` : ""}`}: <strong>{ location.base_salary_ba ? currencyFormat(location.base_salary_ba) : 'Not in SERB'}</strong>
                                      </div>
                                      <div className={'choropleth-var'}>
                                          Accumulated earnings (BA){`${year?.value === "MCD" ? `(${location.mcd_year})` : ""}`}: <strong>{ location.lifetime_earnings_ba ? currencyFormat(location.lifetime_earnings_ba) : 'Not in SERB'}</strong>
                                      </div>
                                      <div className={'choropleth-var'}>
                                          Estimated time to earn $50K (BA){`${year?.value === "MCD" ? `(${location.mcd_year})` : ""}`}: <strong>{ location.years_to_amount_ba ?? 'Not in SERB'}</strong>
                                      </div>
                                  </>
                              )
                          }
                          {
                              type === 'salary_ma' && (
                                  <>
                                      <div className={'choropleth-var'}>
                                          Base Salary (MA){`${year?.value === "MCD" ? `(${location.mcd_year})` : ""}`}: <strong>{ location.base_salary_ma ? currencyFormat(location.base_salary_ma) : 'Not in SERB'}</strong>
                                      </div>
                                      <div className={'choropleth-var'}>
                                          Accumulated earnings (MA){`${year?.value === "MCD" ? `(${location.mcd_year})` : ""}`}: <strong>{ location.lifetime_earnings_ma ? currencyFormat(location.lifetime_earnings_ma) : 'Not in SERB'}</strong>
                                      </div>
                                      <div className={'choropleth-var'}>
                                          Estimated time to earn $50K (MA){`${year?.value === "MCD" ? `(${location.mcd_year})` : ""}`}: <strong>{ location.years_to_amount_ma ?? 'Not in SERB'}</strong>
                                      </div>
                                  </>
                              )
                          }
                          {
                              type === 'live_birth' && (
                                  <>
                                      <div>Births ({location.lb_focal_year}): <strong>{location.lb_births != null ? internationalNumberFormat.format(location.lb_births) : nullText("NR")}</strong></div>
                                      <div>{location.lb_grade_label ?? 'KG'} Enrollment ({location.lb_focal_year ? location.lb_focal_year + (location.lb_offset ?? 6) : ''}): <strong>{(location.lb_enroll ?? location.lb_kg) != null ? internationalNumberFormat.format(location.lb_enroll ?? location.lb_kg) : nullText("NR")}</strong></div>
                                      <div>{location.lb_grade_label ?? 'KG'} Birth Ratio: <strong>{location.persistence_ratio != null ? location.persistence_ratio.toFixed(2) : nullText("NR")}</strong></div>
                                  </>
                              )
                          }
                      </Tooltip>
                    </Polygon>
                )
            )}
        </MapContainer>
      </Box>
  );
};

MapView.propTypes = {
    data: PropTypes.array.isRequired,
    districtIrn: PropTypes.number,
    zoom: PropTypes.number,
    height: PropTypes.string,
    choroplethVar: PropTypes.string,
    type: PropTypes.string,
}

export default React.memo(MapView);
